// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — opening a Khipu someone has on their computer (the Khipu plan,
 * slice 1): a `.kpu` file, or the `.zip` AYNI downloads (the `.kpu`,
 * its CSVs and the licence). Both the full format and the minimal exchange
 * copy open; the exchange copy is filled out to a Khipu that plays the same
 * steps (anonymous, no log). Anything that is not a Khipu, or is larger
 * than the player takes, is refused with a reason, never half-read.
 *
 * Written from scratch for AYNI (owner, 2026-10-05): nothing here comes
 * from TerraViz's code.
 */
import { EXCHANGE_FORMAT, type Khipu, type KhipuMedia, type KhipuStep, type KhipuText } from './khipu'

/** Largest file the player opens (the account's whole store is 16 MB). */
export const MAX_KHIPU_FILE_BYTES = 16 * 1024 * 1024
/** Most steps a Khipu may have here. */
export const MAX_STEPS = 500

export type KhipuFileError = 'too_large' | 'not_a_khipu' | 'unreadable'

export type KhipuFileResult =
  | { ok: true; khipu: Khipu; files: Array<{ name: string; body: string }> }
  | { ok: false; error: KhipuFileError }

const ID = /^[a-f0-9]{16}$/
const KINDS = new Set(['lesson', 'session', 'briefing', 'research', 'story'])
const MEDIA_KINDS = new Set(['image', 'video', 'panorama', 'link'])

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/** A localized text with at least one non-empty string, else undefined. */
function text(v: unknown): KhipuText | undefined {
  if (!isRecord(v)) return undefined
  const out: KhipuText = {}
  for (const [lang, value] of Object.entries(v)) if (typeof value === 'string' && value.trim()) out[lang] = value
  return Object.keys(out).length ? out : undefined
}

/** A step's media, https links only; anything else is dropped. */
function media(v: unknown): KhipuMedia[] | undefined {
  if (!Array.isArray(v)) return undefined
  const out: KhipuMedia[] = []
  for (const m of v) {
    if (!isRecord(m) || typeof m.kind !== 'string' || !MEDIA_KINDS.has(m.kind) || typeof m.url !== 'string') continue
    if (!/^https:\/\//i.test(m.url)) continue
    const title = text(m.title)
    const caption = text(m.caption)
    out.push({
      kind: m.kind as KhipuMedia['kind'],
      url: m.url,
      ...(title ? { title } : {}),
      ...(caption ? { caption } : {}),
      ...(typeof m.credit === 'string' && m.credit.trim() ? { credit: m.credit.slice(0, 300) } : {}),
    })
  }
  return out
}

/**
 * The Khipu in a parsed file, or null when it is not one. Accepts the full
 * format (`ayni-khipu`) and the exchange copy (`ayni-khipu-exchange`); the
 * shape is checked as far as the player relies on it.
 */
export function normalizeKhipu(raw: unknown): Khipu | null {
  if (!isRecord(raw)) return null
  const exchange = raw.format === EXCHANGE_FORMAT
  if (raw.format !== 'ayni-khipu' && !exchange) return null
  if (typeof raw.id !== 'string' || !ID.test(raw.id)) return null
  if (typeof raw.kind !== 'string' || !KINDS.has(raw.kind)) return null
  const title = text(raw.title)
  if (!title) return null
  if (!Array.isArray(raw.datasets) || !isRecord(raw.path) || !Array.isArray(raw.path.steps)) return null
  const steps = raw.path.steps as unknown[]
  if (steps.length === 0 || steps.length > MAX_STEPS) return null
  for (const st of steps) {
    if (!isRecord(st) || typeof st.id !== 'string' || typeof st.n !== 'number') return null
  }
  for (const d of raw.datasets as unknown[]) {
    if (!isRecord(d) || typeof d.key !== 'string' || typeof d.ref !== 'string') return null
  }
  const cleanSteps = (steps as KhipuStep[]).map((st) => {
    const m = media(st.media)
    const { media: _drop, ...rest } = st
    return m && m.length ? { ...rest, media: m } : rest
  })
  const k = { ...(raw as unknown as Khipu), title, path: { ...(raw.path as Khipu['path']), steps: cleanSteps } }
  if (exchange) {
    return {
      ...k,
      format: 'ayni-khipu',
      schemaVersion: '1.0',
      created: typeof raw.created === 'string' ? raw.created : new Date(0).toISOString(),
      privacy: { mode: 'anonymous' },
    }
  }
  if (!isRecord(raw.privacy) || (raw.privacy.mode !== 'anonymous' && raw.privacy.mode !== 'attributed')) return null
  return k
}

/** Read a `.kpu` (or `.json`) file, or a `.zip` holding one. */
export async function readKhipuFile(file: Blob & { name?: string }): Promise<KhipuFileResult> {
  if (file.size > MAX_KHIPU_FILE_BYTES) return { ok: false, error: 'too_large' }
  const name = (file.name ?? '').toLowerCase()
  try {
    const head = new Uint8Array(await file.slice(0, 4).arrayBuffer())
    const isZip = name.endsWith('.zip') || (head[0] === 0x50 && head[1] === 0x4b)
    if (!isZip) {
      const khipu = normalizeKhipu(JSON.parse(await file.text()))
      return khipu ? { ok: true, khipu, files: [] } : { ok: false, error: 'not_a_khipu' }
    }
    const { default: JSZip } = await import('jszip')
    const zip = await JSZip.loadAsync(await file.arrayBuffer())
    const entries = Object.values(zip.files).filter((f) => !f.dir)
    const kpu = entries.find((f) => /\.kpu$/i.test(f.name))
    if (!kpu) return { ok: false, error: 'not_a_khipu' }
    const khipu = normalizeKhipu(JSON.parse(await kpu.async('string')))
    if (!khipu) return { ok: false, error: 'not_a_khipu' }
    const files: Array<{ name: string; body: string }> = []
    for (const f of entries) {
      if (!/\.csv$/i.test(f.name)) continue
      files.push({ name: f.name.split('/').pop() ?? f.name, body: await f.async('string') })
    }
    return { ok: true, khipu, files }
  } catch {
    return { ok: false, error: 'unreadable' }
  }
}
